import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

const Admin = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [activeTab, setActiveTab] = useState('messages');
  
  // Ampel state
  const [leipzigAmpel, setLeipzigAmpel] = useState({
    behandlungspflege: 'green',
    grundpflege: 'green',
    hauswirtschaft: 'green',
    beratung: 'green'
  });
  const [halleAmpel, setHalleAmpel] = useState({
    behandlungspflege: 'green',
    grundpflege: 'green',
    hauswirtschaft: 'green',
    beratung: 'green'
  });
  const [ampelSaving, setAmpelSaving] = useState(false);
  
  // Salary/Konditionen state
  const [leipzigSalary, setLeipzigSalary] = useState({
    pflegehelfer_gehalt: '3.568,87 €',
    pflegefachkraft_gehalt: '4.643,51 €',
    urlaub_tage: 30
  });
  const [halleSalary, setHalleSalary] = useState({
    pflegehelfer_gehalt: '3.568,87 €',
    pflegefachkraft_gehalt: '4.643,51 €',
    urlaub_tage: 30
  });
  const [salarySaving, setSalarySaving] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    window.scrollTo(0, 0);
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('admin_token');
    
    if (!token) {
      navigate('/portal-gsh');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/admin/verify`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setIsAuthenticated(true);
        fetchMessages();
        fetchAmpelStatus();
        fetchSalaryInfo();
      } else {
        localStorage.removeItem('admin_token');
        navigate('/portal-gsh');
      }
    } catch (err) {
      localStorage.removeItem('admin_token');
      navigate('/portal-gsh');
    }
  };

  const fetchMessages = async () => {
    const token = localStorage.getItem('admin_token');
    
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/contact/messages`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('admin_token');
          navigate('/portal-gsh');
          return;
        }
        throw new Error('Fehler beim Laden der Nachrichten');
      }
      
      const data = await response.json();
      data.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      setMessages(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchAmpelStatus = async () => {
    try {
      const [leipzigRes, halleRes] = await Promise.all([
        fetch(`${API_URL}/api/ampel/leipzig`),
        fetch(`${API_URL}/api/ampel/halle`)
      ]);
      
      if (leipzigRes.ok) {
        const data = await leipzigRes.json();
        setLeipzigAmpel({
          behandlungspflege: data.behandlungspflege || 'green',
          grundpflege: data.grundpflege || 'green',
          hauswirtschaft: data.hauswirtschaft || 'green',
          beratung: data.beratung || 'green'
        });
      }
      
      if (halleRes.ok) {
        const data = await halleRes.json();
        setHalleAmpel({
          behandlungspflege: data.behandlungspflege || 'green',
          grundpflege: data.grundpflege || 'green',
          hauswirtschaft: data.hauswirtschaft || 'green',
          beratung: data.beratung || 'green'
        });
      }
    } catch (err) {
      console.error('Failed to fetch ampel status:', err);
    }
  };

  const updateAmpelStatus = async (location, updates) => {
    const token = localStorage.getItem('admin_token');
    setAmpelSaving(true);
    
    try {
      const response = await fetch(`${API_URL}/api/ampel/${location}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });
      
      if (response.ok) {
        // Update local state
        if (location === 'leipzig') {
          setLeipzigAmpel(prev => ({ ...prev, ...updates }));
        } else {
          setHalleAmpel(prev => ({ ...prev, ...updates }));
        }
      }
    } catch (err) {
      console.error('Failed to update ampel status:', err);
    } finally {
      setAmpelSaving(false);
    }
  };

  const fetchSalaryInfo = async () => {
    try {
      const [leipzigRes, halleRes] = await Promise.all([
        fetch(`${API_URL}/api/salary/leipzig`),
        fetch(`${API_URL}/api/salary/halle`)
      ]);
      
      if (leipzigRes.ok) {
        const data = await leipzigRes.json();
        setLeipzigSalary({
          pflegehelfer_gehalt: data.pflegehelfer_gehalt || '3.568,87 €',
          pflegefachkraft_gehalt: data.pflegefachkraft_gehalt || '4.643,51 €',
          urlaub_tage: data.urlaub_tage || 30
        });
      }
      
      if (halleRes.ok) {
        const data = await halleRes.json();
        setHalleSalary({
          pflegehelfer_gehalt: data.pflegehelfer_gehalt || '3.568,87 €',
          pflegefachkraft_gehalt: data.pflegefachkraft_gehalt || '4.643,51 €',
          urlaub_tage: data.urlaub_tage || 30
        });
      }
    } catch (err) {
      console.error('Failed to fetch salary info:', err);
    }
  };

  const updateSalaryInfo = async (location, updates) => {
    const token = localStorage.getItem('admin_token');
    setSalarySaving(true);
    
    try {
      const response = await fetch(`${API_URL}/api/salary/${location}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });
      
      if (response.ok) {
        // Update local state
        if (location === 'leipzig') {
          setLeipzigSalary(prev => ({ ...prev, ...updates }));
        } else {
          setHalleSalary(prev => ({ ...prev, ...updates }));
        }
        alert('Konditionen erfolgreich gespeichert!');
      } else {
        alert('Fehler beim Speichern der Konditionen');
      }
    } catch (err) {
      console.error('Failed to update salary info:', err);
      alert('Fehler beim Speichern der Konditionen');
    } finally {
      setSalarySaving(false);
    }
  };

  const deleteMessage = async (messageId) => {
    console.log('deleteMessage called with ID:', messageId);
    
    if (!messageId) {
      console.error('No message ID provided');
      alert('Fehler: Keine Nachrichten-ID vorhanden');
      return;
    }
    
    if (!window.confirm('Möchten Sie diese Anfrage wirklich löschen?')) {
      return;
    }
    
    const token = localStorage.getItem('admin_token');
    
    if (!token) {
      alert('Sie sind nicht eingeloggt. Bitte melden Sie sich erneut an.');
      return;
    }
    
    try {
      console.log('Sending DELETE request to:', `${API_URL}/api/contact/messages/${messageId}`);
      
      const response = await fetch(`${API_URL}/api/contact/messages/${messageId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Response status:', response.status);
      
      if (response.ok) {
        // Use functional update to ensure we have the latest state
        setMessages(prevMessages => {
          const filtered = prevMessages.filter(m => m.id !== messageId);
          console.log('Messages after delete:', filtered.length);
          return filtered;
        });
        setSelectedMessage(null);
        // Show success feedback
        alert('Nachricht erfolgreich gelöscht!');
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Delete failed:', errorData);
        alert(`Fehler beim Löschen: ${errorData.detail || 'Unbekannter Fehler'}`);
      }
    } catch (err) {
      console.error('Failed to delete message:', err);
      alert('Fehler beim Löschen der Nachricht. Bitte versuchen Sie es erneut.');
    }
  };

  const handleLogout = async () => {
    const token = localStorage.getItem('admin_token');
    
    try {
      await fetch(`${API_URL}/api/admin/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
    } catch (err) {
      // Ignore errors on logout
    }
    
    localStorage.removeItem('admin_token');
    navigate('/portal-gsh');
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStandortLabel = (standort) => {
    return standort === 'halle' ? 'Halle (Saale)' : 'Leipzig';
  };

  const AmpelSelector = ({ location, field, value, onChange }) => {
    const colors = [
      { value: 'green', label: 'Grün', bg: 'bg-green-500', hover: 'hover:bg-green-600' },
      { value: 'yellow', label: 'Gelb', bg: 'bg-yellow-500', hover: 'hover:bg-yellow-600' },
      { value: 'red', label: 'Rot', bg: 'bg-red-500', hover: 'hover:bg-red-600' }
    ];
    
    return (
      <div className="flex gap-2">
        {colors.map(color => (
          <button
            key={color.value}
            onClick={() => onChange(location, { [field]: color.value })}
            className={`w-8 h-8 rounded-full ${color.bg} ${color.hover} transition-all ${
              value === color.value ? 'ring-4 ring-offset-2 ring-gray-400' : 'opacity-50'
            }`}
            title={color.label}
            disabled={ampelSaving}
          />
        ))}
      </div>
    );
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-turquoise-400"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-12 px-4 overflow-hidden bg-gray-900">
        <div className="container mx-auto relative z-10">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-2">
                Admin Dashboard
              </h1>
              <p className="text-gray-300">
                Kontaktanfragen, Ampelsystem und Konditionen verwalten
              </p>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition-colors"
              data-testid="logout-btn"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Abmelden
            </button>
          </div>
        </div>
      </section>

      {/* Tab Navigation */}
      <div className="container mx-auto max-w-6xl px-4 mt-8">
        <div className="flex gap-4 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('messages')}
            className={`pb-4 px-4 font-semibold transition-colors ${
              activeTab === 'messages'
                ? 'text-turquoise-400 border-b-2 border-turquoise-400'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Kontaktanfragen
          </button>
          <button
            onClick={() => setActiveTab('ampel')}
            className={`pb-4 px-4 font-semibold transition-colors ${
              activeTab === 'ampel'
                ? 'text-turquoise-400 border-b-2 border-turquoise-400'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Ampelsystem
          </button>
          <button
            onClick={() => setActiveTab('konditionen')}
            className={`pb-4 px-4 font-semibold transition-colors ${
              activeTab === 'konditionen'
                ? 'text-turquoise-400 border-b-2 border-turquoise-400'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Konditionen
          </button>
        </div>
      </div>

      {/* Content Section */}
      <section className="py-8 px-4">
        <div className="container mx-auto max-w-6xl">
          
          {/* Messages Tab */}
          {activeTab === 'messages' && (
            <>
              {/* Stats */}
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <h3 className="text-gray-500 text-sm font-medium mb-2">Gesamt Anfragen</h3>
                  <p className="text-3xl font-bold text-gray-900">{messages.length}</p>
                </div>
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <h3 className="text-gray-500 text-sm font-medium mb-2">Leipzig</h3>
                  <p className="text-3xl font-bold text-turquoise-400">
                    {messages.filter(m => m.standort === 'leipzig').length}
                  </p>
                </div>
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <h3 className="text-gray-500 text-sm font-medium mb-2">Halle (Saale)</h3>
                  <p className="text-3xl font-bold text-green-500">
                    {messages.filter(m => m.standort === 'halle').length}
                  </p>
                </div>
              </div>

              {/* Refresh Button */}
              <div className="flex justify-end mb-4">
                <button
                  onClick={fetchMessages}
                  className="flex items-center gap-2 bg-turquoise-400 text-white px-4 py-2 rounded-lg hover:bg-turquoise-500 transition-colors"
                  data-testid="refresh-btn"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Aktualisieren
                </button>
              </div>

              {/* Loading State */}
              {loading && (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-turquoise-400 mx-auto"></div>
                  <p className="mt-4 text-gray-600">Lädt Nachrichten...</p>
                </div>
              )}

              {/* Error State */}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-xl mb-6">
                  <p className="font-semibold">Fehler</p>
                  <p>{error}</p>
                </div>
              )}

              {/* Messages List */}
              {!loading && !error && (
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                  {messages.length === 0 ? (
                    <div className="text-center py-12">
                      <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <p className="text-gray-500">Keine Kontaktanfragen vorhanden</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {messages.map((message, index) => (
                        <div 
                          key={message.id || index}
                          className="p-6 hover:bg-gray-50 transition-colors cursor-pointer"
                          onClick={() => setSelectedMessage(selectedMessage?.id === message.id ? null : message)}
                          data-testid={`message-${index}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <h3 className="font-bold text-gray-900">{message.name}</h3>
                                <span className={`px-2 py-1 text-xs rounded-full ${
                                  message.standort === 'halle' 
                                    ? 'bg-green-100 text-green-700' 
                                    : 'bg-turquoise-100 text-turquoise-700'
                                }`}>
                                  {getStandortLabel(message.standort)}
                                </span>
                              </div>
                              <p className="text-gray-600 font-medium mb-1">{message.betreff}</p>
                              <p className="text-gray-500 text-sm">{message.email}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm text-gray-400">{formatDate(message.timestamp)}</p>
                              <svg 
                                className={`w-5 h-5 text-gray-400 mt-2 transition-transform ${selectedMessage?.id === message.id ? 'rotate-180' : ''}`} 
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </div>
                          </div>

                          {/* Expanded Message */}
                          {selectedMessage?.id === message.id && (
                            <div className="mt-4 pt-4 border-t border-gray-100">
                              <div className="grid md:grid-cols-2 gap-4 mb-4">
                                <div>
                                  <p className="text-sm text-gray-500">E-Mail</p>
                                  <a href={`mailto:${message.email}`} className="text-turquoise-400 hover:underline">
                                    {message.email}
                                  </a>
                                </div>
                                <div>
                                  <p className="text-sm text-gray-500">Telefon</p>
                                  {message.telefon ? (
                                    <a href={`tel:${message.telefon}`} className="text-turquoise-400 hover:underline">
                                      {message.telefon}
                                    </a>
                                  ) : (
                                    <span className="text-gray-400">Nicht angegeben</span>
                                  )}
                                </div>
                              </div>
                              <div>
                                <p className="text-sm text-gray-500 mb-2">Nachricht</p>
                                <p className="text-gray-700 whitespace-pre-wrap bg-gray-50 p-4 rounded-lg">
                                  {message.nachricht}
                                </p>
                              </div>
                              <div className="mt-4 flex gap-3">
                                <a
                                  href={`mailto:${message.email}?subject=Re: ${message.betreff}`}
                                  className="bg-turquoise-400 text-white px-4 py-2 rounded-lg hover:bg-turquoise-500 transition-colors flex items-center gap-2"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                  </svg>
                                  Antworten
                                </a>
                                {message.telefon && (
                                  <a
                                    href={`tel:${message.telefon}`}
                                    className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors flex items-center gap-2"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                    </svg>
                                    Anrufen
                                  </a>
                                )}
                                <button
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    console.log('Delete button clicked for message:', message.id);
                                    deleteMessage(message.id);
                                  }}
                                  className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition-colors flex items-center gap-2"
                                  data-testid="delete-message-btn"
                                  type="button"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                  Löschen
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* Ampel Tab */}
          {activeTab === 'ampel' && (
            <div className="space-y-8">
              {/* Leipzig Ampel */}
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <div className="flex items-center gap-3 mb-6">
                  <span className="bg-turquoise-400 text-white w-10 h-10 rounded-full flex items-center justify-center font-bold">L</span>
                  <h2 className="text-2xl font-bold text-gray-900">Leipzig - Freie Kapazitäten</h2>
                </div>
                
                <div className="space-y-4">
                  {[
                    { key: 'behandlungspflege', label: 'Behandlungspflege nach SGB V' },
                    { key: 'grundpflege', label: 'Grundpflege nach SGB XI' },
                    { key: 'hauswirtschaft', label: 'Hauswirtschaft & Betreuung' },
                    { key: 'beratung', label: 'Beratungsbesuche nach §37,3 SGB XI' }
                  ].map(item => (
                    <div key={item.key} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                      <span className="font-medium text-gray-700">{item.label}</span>
                      <AmpelSelector
                        location="leipzig"
                        field={item.key}
                        value={leipzigAmpel[item.key]}
                        onChange={updateAmpelStatus}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Halle Ampel */}
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <div className="flex items-center gap-3 mb-6">
                  <span className="bg-green-500 text-white w-10 h-10 rounded-full flex items-center justify-center font-bold">H</span>
                  <h2 className="text-2xl font-bold text-gray-900">Halle (Saale) - Freie Kapazitäten</h2>
                </div>
                
                <div className="space-y-4">
                  {[
                    { key: 'behandlungspflege', label: 'Behandlungspflege nach SGB V' },
                    { key: 'grundpflege', label: 'Grundpflege nach SGB XI' },
                    { key: 'hauswirtschaft', label: 'Hauswirtschaft & Betreuung' },
                    { key: 'beratung', label: 'Beratungsbesuche nach §37,3 SGB XI' }
                  ].map(item => (
                    <div key={item.key} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                      <span className="font-medium text-gray-700">{item.label}</span>
                      <AmpelSelector
                        location="halle"
                        field={item.key}
                        value={halleAmpel[item.key]}
                        onChange={updateAmpelStatus}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Legend */}
              <div className="bg-gray-100 rounded-xl p-6">
                <h3 className="font-semibold text-gray-700 mb-4">Legende</h3>
                <div className="flex flex-wrap gap-6">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-green-500 rounded-full"></div>
                    <span className="text-gray-600">Grün = Wir nehmen aktuell Klienten auf</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-yellow-500 rounded-full"></div>
                    <span className="text-gray-600">Gelb = Nur noch für akute Notfälle</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-red-500 rounded-full"></div>
                    <span className="text-gray-600">Rot = Keine Kapazitäten</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Konditionen Tab */}
          {activeTab === 'konditionen' && (
            <div className="space-y-8">
              {/* Leipzig Konditionen */}
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <div className="flex items-center gap-3 mb-6">
                  <span className="bg-turquoise-400 text-white w-10 h-10 rounded-full flex items-center justify-center font-bold">L</span>
                  <h2 className="text-2xl font-bold text-gray-900">Leipzig - Konditionen</h2>
                </div>
                
                <div className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Pflegehelfer Gehalt (brutto/Vollzeit)
                      </label>
                      <input
                        type="text"
                        value={leipzigSalary.pflegehelfer_gehalt}
                        onChange={(e) => setLeipzigSalary(prev => ({ ...prev, pflegehelfer_gehalt: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-turquoise-400 focus:border-transparent"
                        placeholder="z.B. 3.568,87 €"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Pflegefachkraft Gehalt (brutto/Vollzeit)
                      </label>
                      <input
                        type="text"
                        value={leipzigSalary.pflegefachkraft_gehalt}
                        onChange={(e) => setLeipzigSalary(prev => ({ ...prev, pflegefachkraft_gehalt: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-turquoise-400 focus:border-transparent"
                        placeholder="z.B. 4.643,51 €"
                      />
                    </div>
                  </div>
                  <div className="md:w-1/2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Urlaubstage
                    </label>
                    <input
                      type="number"
                      value={leipzigSalary.urlaub_tage}
                      onChange={(e) => setLeipzigSalary(prev => ({ ...prev, urlaub_tage: parseInt(e.target.value) || 30 }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-turquoise-400 focus:border-transparent"
                      min="20"
                      max="40"
                    />
                  </div>
                  <div className="pt-4">
                    <button
                      onClick={() => updateSalaryInfo('leipzig', leipzigSalary)}
                      disabled={salarySaving}
                      className="bg-turquoise-400 text-white px-6 py-3 rounded-xl hover:bg-turquoise-500 transition-colors disabled:opacity-50 font-semibold"
                    >
                      {salarySaving ? 'Speichern...' : 'Leipzig Konditionen speichern'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Halle Konditionen */}
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <div className="flex items-center gap-3 mb-6">
                  <span className="bg-green-500 text-white w-10 h-10 rounded-full flex items-center justify-center font-bold">H</span>
                  <h2 className="text-2xl font-bold text-gray-900">Halle (Saale) - Konditionen</h2>
                </div>
                
                <div className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Pflegehelfer Gehalt (brutto/Vollzeit)
                      </label>
                      <input
                        type="text"
                        value={halleSalary.pflegehelfer_gehalt}
                        onChange={(e) => setHalleSalary(prev => ({ ...prev, pflegehelfer_gehalt: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-turquoise-400 focus:border-transparent"
                        placeholder="z.B. 3.568,87 €"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Pflegefachkraft Gehalt (brutto/Vollzeit)
                      </label>
                      <input
                        type="text"
                        value={halleSalary.pflegefachkraft_gehalt}
                        onChange={(e) => setHalleSalary(prev => ({ ...prev, pflegefachkraft_gehalt: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-turquoise-400 focus:border-transparent"
                        placeholder="z.B. 4.643,51 €"
                      />
                    </div>
                  </div>
                  <div className="md:w-1/2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Urlaubstage
                    </label>
                    <input
                      type="number"
                      value={halleSalary.urlaub_tage}
                      onChange={(e) => setHalleSalary(prev => ({ ...prev, urlaub_tage: parseInt(e.target.value) || 30 }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-turquoise-400 focus:border-transparent"
                      min="20"
                      max="40"
                    />
                  </div>
                  <div className="pt-4">
                    <button
                      onClick={() => updateSalaryInfo('halle', halleSalary)}
                      disabled={salarySaving}
                      className="bg-green-500 text-white px-6 py-3 rounded-xl hover:bg-green-600 transition-colors disabled:opacity-50 font-semibold"
                    >
                      {salarySaving ? 'Speichern...' : 'Halle Konditionen speichern'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Info Box */}
              <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                <h3 className="font-semibold text-blue-800 mb-2">Hinweis</h3>
                <p className="text-blue-700 text-sm">
                  Die hier eingegebenen Konditionen werden automatisch auf den Karriere-Seiten angezeigt. 
                  Änderungen sind sofort nach dem Speichern sichtbar.
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Admin;
